import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_REPORT_YEAR = CURRENT_YEAR - 1;
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

/**
 * @param {object} props
 * @param {string} [props.companyName]
 * @param {number|string} [props.companyId] — required for SPOC/admin scoped APIs
 * @param {string} [props.apiPath='/api/company/attrition-report']
 * @param {string} [props.tokenKey='gogig_token']
 * @param {boolean} [props.redirectOnAuthFailure=true]
 * @param {boolean} [props.hideYearSelector=false]
 * @param {boolean} [props.hideHeader=false]
 */
function AttritionReport({
  companyName,
  companyId,
  apiPath = '/api/company/attrition-report',
  tokenKey = 'gogig_token',
  redirectOnAuthFailure = true,
  year: yearProp,
  hideYearSelector = false,
  hideHeader = false,
}) {
  const [selectedYear, setSelectedYear] = useState(yearProp || DEFAULT_REPORT_YEAR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attritionData, setAttritionData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showLeftEmployees, setShowLeftEmployees] = useState(false);

  useEffect(() => {
    if (yearProp != null && Number(yearProp) !== selectedYear) {
      setSelectedYear(Number(yearProp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearProp]);

  useEffect(() => {
    const needsCompany = apiPath.includes('/admin/') || apiPath.includes('/spoc/');
    if (needsCompany && !companyId) {
      setAttritionData(null);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 45000) : null;

    const fetchAttritionData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem(tokenKey);

        if (!token) {
          setError('Authentication required. Please log in again.');
          return;
        }

        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const params = new URLSearchParams({ year: String(selectedYear) });
        if (companyId) params.set('company_id', String(companyId));
        const response = await fetch(
          `${API_URL}${apiPath}?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            ...(controller ? { signal: controller.signal } : {}),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || 'Failed to fetch attrition data');
        }

        const data = await response.json();
        if (cancelled) return;

        if (!data.success) {
          setAttritionData(null);
          setError(data.message || 'No attrition data available');
          return;
        }

        setAttritionData(data);
      } catch (err) {
        if (cancelled) return;
        if (err.name === 'AbortError') {
          setError('Attrition report timed out. Please try again.');
          setAttritionData(null);
        } else if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message.includes('ERR_CONNECTION_REFUSED')) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Connection error: Backend server may not be running on port 5001');
          }
          setError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
          setAttritionData(null);
        } else if (err.message.includes('401') || err.message.includes('403') || err.message.includes('Invalid token') || err.message.includes('Unauthorized')) {
          if (redirectOnAuthFailure && tokenKey === 'gogig_token') {
            localStorage.removeItem('gogig_token');
            localStorage.removeItem('gogig_user');
            setError('Session expired. Please log in again.');
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
          } else {
            setError(err.message || 'Not authorized to view this attrition report.');
          }
          setAttritionData(null);
        } else if (err.message.includes('404') || err.message.includes('not found')) {
          setError('No attrition data found for this year.');
          setAttritionData(null);
        } else {
          console.error('Error fetching attrition data:', err);
          setError('Error fetching attrition data: ' + (err.message || 'Unknown error occurred'));
          setAttritionData(null);
        }
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchAttritionData();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (controller) controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, companyId, apiPath]);

  const handleMonthClick = (monthData) => {
    if (monthData.leftEmployees && monthData.leftEmployees.length > 0) {
      setSelectedMonth(monthData);
      setShowLeftEmployees(true);
    }
  };

  const renderSummaryCards = () => {
    if (!attritionData?.summary) return null;

    const { summary } = attritionData;

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Employees</Typography>
              </Box>
              <Typography variant="h3">{summary.totalEmployees}</Typography>
              <Typography variant="body2">in {selectedYear}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'error.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonRemoveIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Attrition</Typography>
              </Box>
              <Typography variant="h3">{summary.totalAttrition}</Typography>
              <Typography variant="body2">employees left</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDownIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Attrition Rate</Typography>
              </Box>
              <Typography variant="h3">{summary.averageAttritionRate}%</Typography>
              <Typography variant="body2">monthly average</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Tenure</Typography>
              </Box>
              <Typography variant="h3">{summary.averageTenureMonths}</Typography>
              <Typography variant="body2">months</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAnalysis = () => {
    const analysis = attritionData?.analysis;
    if (!analysis) return null;
    const missing = analysis.missingMonths || [];
    const formats = analysis.dateFormatsSeen || {};

    return (
      <Card sx={{ mb: 3, border: '1px solid #99f6e4' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={700}>
            Stipend coverage analysis ({selectedYear})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {analysis.note}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Stipend CSV records</Typography>
              <Typography fontWeight={700}>{analysis.stipendCsvRecordCount ?? 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Unique mobiles (year)</Typography>
              <Typography fontWeight={700}>{attritionData?.summary?.totalEmployees ?? 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Months with CSV</Typography>
              <Typography fontWeight={700}>{attritionData?.summary?.monthsWithStipendData ?? 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Rows skipped (no mobile)</Typography>
              <Typography fontWeight={700} color={analysis.rowsSkippedNoMobile ? 'warning.main' : 'inherit'}>
                {analysis.rowsSkippedNoMobile ?? 0}
              </Typography>
            </Grid>
          </Grid>

          {analysis.identityNote && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {analysis.identityNote}
            </Alert>
          )}

          {Object.keys(formats).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Date formats detected</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(formats).map(([fmt, count]) => (
                  <Chip key={fmt} size="small" label={`${fmt}: ${count}`} variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {missing.length > 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <strong>No stipend CSV for:</strong>{' '}
              {missing.map((m) => m.monthName).join(', ')}.
              {' '}These months are not compared for attrition (avoids fake 100% drop).
            </Alert>
          ) : (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Stipend CSV found for all 12 months in {selectedYear}.
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMonthlyTable = () => {
    if (!attritionData?.monthlyAttrition) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Attrition Breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Stipend CSV</TableCell>
                  <TableCell align="right">Active (mobile)</TableCell>
                  <TableCell align="right">Same (retained)</TableCell>
                  <TableCell align="right">Joined</TableCell>
                  <TableCell align="right">Left</TableCell>
                  <TableCell align="right">Attrition Rate</TableCell>
                  <TableCell align="right">Retention Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attritionData.monthlyAttrition.map((monthData) => {
                  const missing = monthData.hasStipendData === false;
                  return (
                    <TableRow
                      key={monthData.month}
                      hover
                      sx={{
                        cursor: monthData.employeesLeft > 0 ? 'pointer' : 'default',
                        bgcolor: missing ? 'action.hover' : undefined,
                        opacity: missing ? 0.75 : 1,
                      }}
                      onClick={() => handleMonthClick(monthData)}
                    >
                      <TableCell>{monthData.monthName}</TableCell>
                      <TableCell>
                        {missing ? (
                          <Chip label="Missing" size="small" color="warning" />
                        ) : (
                          <Chip label="CSV" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">{monthData.totalEmployees}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={monthData.employeesRetained ?? 0}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={monthData.employeesJoined} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={monthData.employeesLeft}
                          size="small"
                          color="error"
                          variant={monthData.employeesLeft > 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {missing || monthData.comparisonSkipped ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          <Typography
                            color={parseFloat(monthData.attritionRate) > 5 ? 'error' : 'text.primary'}
                            fontWeight={parseFloat(monthData.attritionRate) > 5 ? 'bold' : 'normal'}
                          >
                            {monthData.attritionRate}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {missing || monthData.comparisonSkipped ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          <Typography
                            color={parseFloat(monthData.retentionRate) > 95 ? 'success.main' : 'text.primary'}
                            fontWeight={parseFloat(monthData.retentionRate) > 95 ? 'bold' : 'normal'}
                          >
                            {monthData.retentionRate}%
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ py: hideHeader ? 0 : 3 }}>
      {!hideHeader && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={700} color="#0f766e">
            Employee Attrition Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track employee turnover and retention metrics for {companyName || attritionData?.company?.name || 'your company'}
          </Typography>
        </Box>
      )}

      {!hideYearSelector && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {YEARS.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : attritionData?.summary && Number(attritionData.summary.totalEmployees) === 0 ? (
        <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
          No attrition data for {selectedYear}.
          {selectedYear === CURRENT_YEAR ? ` Try ${DEFAULT_REPORT_YEAR} — most companies have stipend data through late ${DEFAULT_REPORT_YEAR}.` : ''}
          {' '}Ensure your establishment code is set in Company Profile so we can match finance stipend CSV uploads (Aisect + Eduvantage).
        </Alert>
      ) : (
        <>
          {renderSummaryCards()}
          {renderAnalysis()}
          {renderMonthlyTable()}
        </>
      )}

      {/* Dialog to show employees who left */}
      <Dialog 
        open={showLeftEmployees} 
        onClose={() => setShowLeftEmployees(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Employees Who Left - {selectedMonth?.monthName}
        </DialogTitle>
        <DialogContent>
          {selectedMonth?.leftEmployees && selectedMonth.leftEmployees.length > 0 ? (
            <List>
              {selectedMonth.leftEmployees.map((employee, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={employee.name || 'Unknown'}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          Mobile: {employee.mobile || 'N/A'}
                        </Typography>
                        {' — '}
                        <Typography component="span" variant="body2">
                          Tenure: {employee.tenure} month{employee.tenure !== 1 ? 's' : ''}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No employees left this month</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLeftEmployees(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AttritionReport;

